import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/hooks/use-language';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { Send, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Member {
  user_id: string;
  first_name: string;
  last_name: string;
}

interface CommentInputProps {
  eventId: string;
  onCommentAdded?: () => void;
  replyTo?: { commentId: string; userName: string } | null;
  onCancelReply?: () => void;
}

export function CommentInput({ eventId, onCommentAdded, replyTo, onCancelReply }: CommentInputProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Search members when @ is typed
  useEffect(() => {
    const searchMembers = async () => {
      if (!mentionSearch) {
        setMembers([]);
        return;
      }

      const { data } = await supabase
        .from('profiles_public_v')
        .select('user_id, first_name, last_name')
        .or(`first_name_search.ilike.%${mentionSearch}%,last_name_search.ilike.%${mentionSearch}%`)
        .limit(10);

      setMembers(data || []);
    };

    if (showMentions) {
      searchMembers();
    }
  }, [mentionSearch, showMentions]);

  // Reset selected index when members change
  useEffect(() => {
    setSelectedIndex(0);
  }, [members]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setContent(newContent);
    setCursorPosition(cursorPos);

    // Check if user typed @
    const textBeforeCursor = newContent.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      // Only show mentions if @ is at start or after a space, and no space after @
      const isValidMention = (lastAtIndex === 0 || textBeforeCursor[lastAtIndex - 1] === ' ') && 
                            !textAfterAt.includes(' ');
      
      if (isValidMention) {
        setShowMentions(true);
        setMentionSearch(textAfterAt);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle mention navigation
    if (showMentions && members.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % members.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + members.length) % members.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(members[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    
    // Normal submit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const insertMention = (member: Member) => {
    const textBeforeCursor = content.slice(0, cursorPosition);
    const textAfterCursor = content.slice(cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    const mentionText = `@${member.first_name} ${member.last_name}`;
    const newContent = 
      content.slice(0, lastAtIndex) + 
      mentionText + 
      ' ' + 
      textAfterCursor;
    
    setContent(newContent);
    setShowMentions(false);
    setMentionSearch('');
    
    // Add to mentioned users if not already there
    if (!mentionedUsers.includes(member.user_id)) {
      setMentionedUsers([...mentionedUsers, member.user_id]);
    }

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newCursorPos = lastAtIndex + mentionText.length + 1;
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!user || !content.trim()) return;

    setIsSubmitting(true);
    try {
      // Insert comment
      const { data: comment, error } = await supabase
        .from('event_comments')
        .insert({
          event_id: eventId,
          user_id: user.id,
          content: content.trim(),
          mentioned_users: mentionedUsers,
          parent_comment_id: replyTo?.commentId || null
        })
        .select()
        .single();

      if (error) throw error;

      // Send notifications to mentioned users
      if (mentionedUsers.length > 0 && comment) {
        const { data: eventData } = await supabase
          .from('events')
          .select('title')
          .eq('id', eventId)
          .single();

        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('user_id', user.id)
          .single();

        const commenterName = profile ? `${profile.first_name} ${profile.last_name}` : 'Someone';

        // Create notifications for mentioned users
        const notifications = mentionedUsers.map(userId => ({
          user_id: userId,
          type: 'event',
          title: t('event.comments.mention_notification', { name: commenterName }),
          description: `"${eventData?.title || 'Event'}"`,
          action_url: `/event/${eventId}`,
          priority: 'medium',
          metadata: {
            eventId,
            commentId: comment.id,
            commenterId: user.id
          }
        }));

        await supabase.from('notifications').insert(notifications);
      }

      // Clear form
      setContent('');
      setMentionedUsers([]);
      onCancelReply?.();
      toast({
        title: t('event.comments.success') || 'Comment posted',
        duration: 2000,
      });

      onCommentAdded?.();
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: t('event.comments.error') || 'Failed to post comment',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      {replyTo && (
        <div className="mb-2 p-2 bg-muted rounded-md flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('event.comments.replying_to', { name: replyTo.userName })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelReply}
            className="h-6 px-2 text-xs"
          >
            {t('event.comments.cancel_reply')}
          </Button>
        </div>
      )}
      <Textarea
        ref={textareaRef}
        value={content}
        onChange={handleContentChange}
        onKeyDown={handleKeyDown}
        placeholder={t('event.comments.placeholder')}
        className="min-h-[100px] resize-none"
        disabled={isSubmitting}
      />
      
      {showMentions && members.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {members.map((member, index) => (
            <button
              key={member.user_id}
              onClick={() => insertMention(member)}
              className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors ${
                index === selectedIndex ? 'bg-accent' : ''
              }`}
            >
              <span className="font-medium">{member.first_name} {member.last_name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-2 flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !content.trim()}
          size="sm"
        >
          <Send className="w-4 h-4 mr-2" />
          {t('event.comments.submit')}
        </Button>
      </div>
    </div>
  );
}
